import { useEffect, useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'

import { CurrencyMinimalAmount, SettingsKeys } from 'models/settings'
import { SpinnerError } from 'models/calculator'

import { fetchSettings } from 'services/settings'

export type SettingsConfig = {
  SETTINGS_KEYS: SettingsKeys[]
  DEFAULT_MINIMAL_AMOUNT: CurrencyMinimalAmount
}

interface SettingsHookProps {
  setSpinnerError: (error: SpinnerError) => void
  config: SettingsConfig
}

export const useSettings = ({ setSpinnerError, config }: SettingsHookProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCurrentCurrencySupported, setIsCurrentCurrencySupported] = useState(true)
  const [supportedCurrenciesPairs, setSupportedCurrenciesPairs] = useState<string[]>([])
  const [allCurrencies, setAllCurrencies] = useState<string[]>([])
  const [minimalAmounts, setMinimalAmounts] = useState<CurrencyMinimalAmount[]>([config.DEFAULT_MINIMAL_AMOUNT])

  useEffect(() => {
    const fetchSupportedCurrency = async () => {
      try {
        const response = await fetchSettings(config.SETTINGS_KEYS)
        const supportedPairs: string[] = response['market-currency-pairs']?.pairs ?? []
        const allPairs: string[] = []

        supportedPairs.forEach((pair: string) => {
          const base = pair.slice(3)
          const counter = pair.slice(0, 3)
          allPairs.push(`${base}${counter}`, `${counter}${base}`)
        })

        setSupportedCurrenciesPairs(allPairs)
        setAllCurrencies(response['currency']?.symbols ?? [])
        setMinimalAmounts(
          response['direct-exchange-minimal-amounts']?.['minimal_amounts'] ?? [config.DEFAULT_MINIMAL_AMOUNT],
        )
      } catch (e: any) {
        const status = e?.status ?? e?.response?.status
        if (status === 429) {
          setSpinnerError({
            isErrorVisible: true,
            message: i18n('calculator.spinnerError.toManyRequests'),
          })
        } else {
          setSpinnerError({ isErrorVisible: true })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchSupportedCurrency()
  }, [])

  const isCurrencyPairSupported = (baseCurrency: string, counterCurrency: string): boolean => {
    const userPair = `${baseCurrency}${counterCurrency}`
    const supported = supportedCurrenciesPairs.includes(userPair)
    return supported
  }

  const isCurrencySupported = (currency: string): boolean => {
    return allCurrencies.includes(currency)
  }

  return {
    isSettingsLoading: isLoading,
    isCurrentCurrencySupported,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isCurrencySupported,
    supportedCurrenciesPairs,
    allCurrencies,
    minimalAmounts,
  }
}
